import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { match, Pattern } from 'ts-pattern';

import { useResetPassword } from '~/api/auth';
import { FetchError, HttpBusinessConflictError, HttpError, HttpTooManyRequestsError } from '~/api/errors';
import { Alert, Button, ErrorMessage, ExternalLink, Redirect } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { SearchParamKeys } from '~/constants/url';
import { routes } from '~/providers/RouterProvider/router.routes';

import { AuthLayoutContent } from '../AuthLayout/AuthLayoutContent';
import { AuthLayoutTitleGroup } from '../AuthLayout/AuthLayoutTitleGroup';
import { PasswordField } from '../PasswordField/PasswordField';
import { formStyle } from './ResetPassword.css';
import { parsePasswordResetToken } from './ResetPassword.utils';

export const ResetPasswordGuard = () => {
  const [searchParams] = useSearchParams();
  const encodedToken = searchParams.get(SearchParamKeys.RESET_PASSWORD_TOKEN);

  if (!encodedToken) return <Redirect to={routes.login} />;

  const parsedToken = parsePasswordResetToken(encodedToken);

  if (!parsedToken || dayjs().isAfter(parsedToken.expiredAt))
    return (
      <Redirect
        state={{
          isPasswordResetTokenExpired: true,
          passwordResetEmailAddress: parsedToken?.emailAddress,
        }}
        to={routes.forgotPassword}
      />
    );

  const { emailAddress, token } = parsedToken;

  return <ResetPassword emailAddress={emailAddress} token={token} />;
};

type Props = {
  emailAddress: string;
  token: string;
};

const ResetPassword = ({ emailAddress, token }: Props) => {
  const mutation = useResetPassword();

  const { control, handleSubmit } = useForm<{ password: string }>({
    defaultValues: { password: '' },
  });

  const { t } = useTranslation(['auth', 'common', 'validation']);

  const onSubmit = handleSubmit(({ password }) => {
    mutation.mutate({ token, emailAddress, password });
  });

  return (
    <AuthLayoutContent>
      <AuthLayoutTitleGroup subtitle={t('auth:resetPasswordPage.header.subtitle')} title={t('auth:resetPasswordPage.header.title')} />

      <Form className={formStyle} onSubmit={onSubmit}>
        <Field.Root>
          <Field.Label>{t('auth:loginPage.form.emailField.label')}</Field.Label>

          <Input disabled type="email" value={emailAddress} />
        </Field.Root>

        <PasswordField control={control} />

        {mutation.isError &&
          match(mutation.error)
            .with(
              Pattern.instanceOf(HttpBusinessConflictError),
              (error) => error.errorCode === 'PasswordResetTokenDoesNotExist',
              () => (
                <Redirect
                  state={{
                    isPasswordResetTokenExpired: true,
                    passwordResetEmailAddress: emailAddress,
                  }}
                  to={routes.forgotPassword}
                />
              ),
            )
            .with(
              Pattern.instanceOf(HttpBusinessConflictError),
              (error) => error.errorCode === 'IneligibleUserCannotResetPassword',
              () => (
                <Alert announce icon="Info" status="info" title={t('auth:passwordResetErrors.ineligible.title')}>
                  {t('auth:passwordResetErrors.ineligible.description')}
                </Alert>
              ),
            )
            .with(
              Pattern.instanceOf(HttpBusinessConflictError),
              (error) => error.errorCode === 'UserWithUnverifiedEmailCannotResetPassword',
              () => (
                <Alert announce icon="Info" status="info" title={t('auth:passwordResetErrors.unverifiedEmail.title')}>
                  {t('auth:passwordResetErrors.unverifiedEmail.description')}
                </Alert>
              ),
            )
            .with(
              Pattern.instanceOf(HttpBusinessConflictError),
              (error) => error.errorCode === 'RejectedUserCannotResetPassword',
              () => (
                <Alert announce icon="Error" status="danger" title={t('auth:passwordResetErrors.rejected.title')}>
                  {t('auth:passwordResetErrors.rejected.description')}
                </Alert>
              ),
            )
            .with(Pattern.instanceOf(HttpTooManyRequestsError), () => <ErrorMessage>{t('common:errors.tooManyRequests')}</ErrorMessage>)
            .with(Pattern.instanceOf(HttpError), () => (
              <ErrorMessage>
                <Trans components={{ email: <ExternalLink /> }} i18nKey="common:errors.httpErrorWithLink" />
              </ErrorMessage>
            ))
            .with(Pattern.instanceOf(FetchError), () => <ErrorMessage>{t('common:errors.fetchError')}</ErrorMessage>)
            .otherwise(() => <ErrorMessage>{t('common:errors.unknown')}</ErrorMessage>)}

        <Button loading={mutation.isPending} type="submit">
          {t('auth:resetPasswordPage.form.submitButton')}
        </Button>
      </Form>
    </AuthLayoutContent>
  );
};
